/* Partner Hardware Portal — quantities, summary, cart operations.
 *
 * Integrates with the Warehouse theme's cart, never replaces it:
 *  - adds via POST {routes.cartAddUrl}.js with an items[] payload
 *    (the multi-item form proven in ha64-sp-cart.js)
 *  - then dispatches a bubbling 'product:added' CustomEvent whose
 *    detail.quantity is MANDATORY (theme.js Cart#_onProductAdded adds
 *    it to the header count) — the theme handles drawer refresh + open
 *  - provisioning-line quantity is NOT managed here; the global
 *    pp-cart-sync.js reconciles it after every cart re-render, keyed
 *    off private line properties and the _pp_prov_variant attribute
 *    written below.
 *
 * Pricing note: subtotal math reads data-price (cents) rendered
 * server-side from the live variant price. When partner pricing lands,
 * only the Liquid that writes data-price needs to change.
 */
(function () {
  'use strict';

  var MAX_QTY = 999;

  function money(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  function init() {
    var root = document.querySelector('[data-pp-portal]');
    if (!root) return;

    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-pp-item]'));
    if (!rows.length) return;

    var summaryList = root.querySelector('[data-pp-summary-list]');
    var summaryEmpty = root.querySelector('[data-pp-summary-empty]');
    var summaryBody = root.querySelector('[data-pp-summary-body]');
    var subtotalEl = root.querySelector('[data-pp-subtotal]');
    var provRow = root.querySelector('[data-pp-prov-row]');
    var provAmount = root.querySelector('[data-pp-prov-amount]');
    var addAllBtn = root.querySelector('[data-pp-add-all]');
    var feedback = root.querySelector('[data-pp-feedback]');
    var sticky = root.querySelector('[data-pp-sticky]');
    var stickyCount = root.querySelector('[data-pp-sticky-count]');
    var stickyTotal = root.querySelector('[data-pp-sticky-total]');

    var provVariant = parseInt(root.dataset.provVariant, 10) || 0;
    var provPrice = parseInt(root.dataset.provPrice, 10) || 0;
    var provLabel = root.dataset.provLabel || '8x8 Phone Provisioning';
    var attribution = {
      'Partner Program': root.dataset.attrPartner || '',
      'Platform': root.dataset.attrPlatform || '',
      'Source': root.dataset.attrSource || '',
      'Partner': root.dataset.attrCommission || ''
    };

    var busy = false;
    var attributesWritten = false;
    var partnerCodes = (root.dataset.partnerCodes || '').replace(/\s+/g, '');

    // Partner pricing: the codes set the partner prices at checkout. Prime the
    // session's discount cookie now so any checkout path picks them up.
    if (partnerCodes) {
      fetch('/discount/' + encodeURIComponent(partnerCodes), { credentials: 'same-origin' }).catch(function () {});
    }

    /* ---------- state ---------- */

    function rowInfo(row) {
      return {
        row: row,
        variantId: parseInt(row.dataset.variantId, 10),
        price: parseInt(row.dataset.price, 10) || 0,
        title: row.dataset.title || '',
        provision: row.dataset.provision === '1',
        available: row.dataset.available === '1',
        qty: parseInt(row.querySelector('[data-pp-qty]') ? row.querySelector('[data-pp-qty]').value : '0', 10) || 0
      };
    }

    function selections() {
      return rows.map(rowInfo).filter(function (r) { return r.available && r.qty > 0; });
    }

    /* ---------- quantity controls ---------- */

    function clamp(n) {
      n = parseInt(n, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (n > MAX_QTY) n = MAX_QTY;
      return n;
    }

    function setQty(row, qty) {
      var input = row.querySelector('[data-pp-qty]');
      if (!input) return;
      qty = clamp(qty);
      input.value = String(qty);
      row.classList.toggle('pp-row--selected', qty > 0);
      var dec = row.querySelector('[data-pp-dec]');
      if (dec) dec.disabled = qty === 0;
      renderSummary();
    }

    rows.forEach(function (row) {
      var input = row.querySelector('[data-pp-qty]');
      var dec = row.querySelector('[data-pp-dec]');
      var inc = row.querySelector('[data-pp-inc]');
      if (!input) return;
      if (dec) dec.addEventListener('click', function () { setQty(row, clamp(input.value) - 1); });
      if (inc) inc.addEventListener('click', function () { setQty(row, clamp(input.value) + 1); });
      input.addEventListener('change', function () { setQty(row, input.value); });
      input.addEventListener('input', function () {
        // strip non-digits live, defer clamping to change
        input.value = input.value.replace(/[^0-9]/g, '');
      });
      setQty(row, input.value);
    });

    /* ---------- summary ---------- */

    function renderSummary() {
      var sel = selections();
      var units = sel.reduce(function (a, r) { return a + r.qty; }, 0);
      var subtotal = sel.reduce(function (a, r) { return a + r.qty * r.price; }, 0);
      var provQty = sel.reduce(function (a, r) { return a + (r.provision ? r.qty : 0); }, 0);
      var provTotal = provVariant ? provQty * provPrice : 0;

      if (summaryList) {
        summaryList.textContent = '';
        sel.forEach(function (r) {
          var li = document.createElement('li');
          li.className = 'pp-summary__line';
          var label = document.createElement('span');
          label.textContent = r.qty + ' × ' + r.title;
          var amount = document.createElement('span');
          amount.className = 'pp-summary__line-amount';
          amount.textContent = money(r.qty * r.price);
          li.appendChild(label);
          li.appendChild(amount);
          summaryList.appendChild(li);
        });
      }

      if (provRow) {
        provRow.hidden = !(provVariant && provQty > 0);
        if (provAmount) provAmount.textContent = provQty + ' × ' + provLabel + ' — ' + (provTotal === 0 ? 'FREE' : money(provTotal));
      }

      var grand = subtotal + provTotal;
      if (subtotalEl) subtotalEl.textContent = money(grand);
      if (summaryEmpty) summaryEmpty.hidden = sel.length > 0;
      if (summaryBody) summaryBody.hidden = sel.length === 0;
      if (addAllBtn) addAllBtn.setAttribute('aria-disabled', sel.length ? 'false' : 'true');

      if (sticky) {
        if (units > 0) sticky.setAttribute('data-visible', ''); else sticky.removeAttribute('data-visible');
        if (stickyCount) stickyCount.textContent = units + (units === 1 ? ' item selected' : ' items selected');
        if (stickyTotal) stickyTotal.textContent = money(grand);
      }
    }

    /* ---------- feedback ---------- */

    function say(msg, kind) {
      if (!feedback) return;
      feedback.textContent = msg;
      feedback.className = 'pp-feedback' + (kind ? ' pp-feedback--' + kind : '');
    }

    /* ---------- cart operations ---------- */

    function cartUrl(path) {
      var base = (window.routes && window.routes.cartUrl) || '/cart';
      return base + path;
    }

    function writeAttributes() {
      if (attributesWritten) return Promise.resolve();
      var attrs = {};
      Object.keys(attribution).forEach(function (k) { if (attribution[k]) attrs[k] = attribution[k]; });
      if (provVariant) attrs['_pp_prov_variant'] = String(provVariant);
      if (partnerCodes) attrs['_pp_codes'] = partnerCodes;
      if (partnerCodes && root.dataset.sfToken) attrs['_pp_sf'] = root.dataset.sfToken;
      // Accessory upsells for the cart drawer (read by snippets/pp-drawer-offers.liquid)
      var upsellLabels = {};
      var upsellOrder = [];
      root.querySelectorAll('[data-pp-upsell-handle]').forEach(function (row) {
        var h = row.getAttribute('data-pp-upsell-handle');
        if (!h) return;
        var label = row.getAttribute('data-pp-upsell-label') || '';
        if (!(h in upsellLabels)) {
          upsellLabels[h] = label;
          upsellOrder.push(h);
        } else if (upsellLabels[h] !== label) {
          // same product offered under two phone-specific labels (e.g. the
          // shared power adapter) -> blank label, drawer falls back to the
          // product's own title
          upsellLabels[h] = '';
        }
      });
      if (upsellOrder.length) {
        attrs['_pp_upsells'] = upsellOrder.map(function (h) { return h + '~' + upsellLabels[h]; }).join('|');
      }
      if (!Object.keys(attrs).length) return Promise.resolve();
      return fetch(cartUrl('/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function () { attributesWritten = true; });
    }

    function addItems(list, buttons) {
      if (busy || !list.length) return;
      busy = true;
      buttons.forEach(function (b) { b.classList.add('pp__btn--busy'); b.setAttribute('aria-disabled', 'true'); });
      say('Adding to cart…', null);

      var items = list.map(function (r) {
        var props = { '_pp_portal': attribution['Source'] || 'partner-portal' };
        if (r.provision) props['_pp_provision'] = '1';
        return { id: r.variantId, quantity: r.qty, properties: props };
      });
      var units = list.reduce(function (a, r) { return a + r.qty; }, 0);

      writeAttributes()
        .then(function () {
          var addUrl = ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';
          return fetch(addUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (response) {
          if (response.ok) {
            // Theme contract: Cart#_onProductAdded requires detail.quantity.
            // It refreshes the drawer, updates the header count and opens it.
            root.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true,
              detail: { quantity: units }
            }));
            // Deterministic provisioning sync -- do not rely on the theme's
            // cart:rerendered event reaching us (it missed on repeat adds).
            if (window.__ppCartSyncNow) window.__ppCartSyncNow();
            say('Added ' + units + (units === 1 ? ' item' : ' items') + ' to your cart.', 'ok');
          } else {
            return response.json().then(function (err) {
              say(err.description || err.message || 'Could not add to cart. Please try again.', 'error');
            });
          }
        })
        .catch(function () {
          say('Could not reach the cart. Check your connection and try again.', 'error');
        })
        .then(function () {
          busy = false;
          buttons.forEach(function (b) { b.classList.remove('pp__btn--busy'); b.removeAttribute('aria-disabled'); });
          renderSummary();
        });
    }

    /* per-card add: uses the row's quantity, or 1 if none selected */
    root.querySelectorAll('[data-pp-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        var row = btn.closest('[data-pp-item]');
        if (!row) return;
        var info = rowInfo(row);
        if (!info.available) return;
        if (info.qty === 0) info.qty = 1;
        addItems([info], [btn]);
      });
    });

    /* add everything selected */
    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        if (addAllBtn.getAttribute('aria-disabled') === 'true') return;
        var sel = selections();
        if (!sel.length) {
          say('Choose quantities above first — every item is optional.', 'error');
          return;
        }
        addItems(sel, [addAllBtn]);
      });
    }

    var stickyBtn = root.querySelector('[data-pp-sticky-add]');
    if (stickyBtn && addAllBtn) {
      stickyBtn.addEventListener('click', function () { addAllBtn.click(); });
    }

    renderSummary();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
